import React, { useState } from 'react';
import {
  Sparkles,
  Wand2,
  Maximize2,
  Minimize2,
  Languages,
  Flame,
  Check,
  Loader2,
  Copy,
} from 'lucide-react';
import { aiClientService } from '../../services/aiService';

interface AICopywriterModalProps {
  initialText: string;
  contextTitle?: string;
  onClose: () => void;
  onApply: (newText: string) => void;
}

export const AICopywriterModal: React.FC<AICopywriterModalProps> = ({
  initialText,
  contextTitle = 'Metin Alanı',
  onClose,
  onApply,
}) => {
  const [currentText, setCurrentText] = useState(initialText);
  const [improvedText, setImprovedText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleRewrite = async (
    action: 'IMPROVE' | 'EXPAND' | 'SUMMARIZE' | 'PUNCHY' | 'TRANSLATE_EN' | 'TRANSLATE_TR'
  ) => {
    setIsLoading(true);
    try {
      const res = await aiClientService.rewriteCopy(currentText, action, contextTitle);
      setImprovedText(res);
    } catch (err) {
      console.error('Rewrite failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(improvedText || currentText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-xl bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-800 bg-slate-950 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-xs font-bold text-white">AI Metin Editörü & Copywriter</h3>
              <p className="text-[11px] text-slate-400">{contextTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-800 transition"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Action pills */}
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => handleRewrite('IMPROVE')}
              disabled={isLoading || !currentText.trim()}
              className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-indigo-600 text-slate-200 hover:text-white text-xs font-medium flex items-center gap-1.5 transition disabled:opacity-50"
            >
              <Wand2 className="w-3.5 h-3.5 text-indigo-400" /> Profesyonel İyileştir
            </button>
            <button
              type="button"
              onClick={() => handleRewrite('PUNCHY')}
              disabled={isLoading || !currentText.trim()}
              className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-amber-600 text-slate-200 hover:text-white text-xs font-medium flex items-center gap-1.5 transition disabled:opacity-50"
            >
              <Flame className="w-3.5 h-3.5 text-amber-400" /> Çarpıcı & Punchy
            </button>
            <button
              type="button"
              onClick={() => handleRewrite('EXPAND')}
              disabled={isLoading || !currentText.trim()}
              className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-indigo-600 text-slate-200 hover:text-white text-xs font-medium flex items-center gap-1.5 transition disabled:opacity-50"
            >
              <Maximize2 className="w-3.5 h-3.5 text-sky-400" /> Genişlet & Detaylandır
            </button>
            <button
              type="button"
              onClick={() => handleRewrite('SUMMARIZE')}
              disabled={isLoading || !currentText.trim()}
              className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-indigo-600 text-slate-200 hover:text-white text-xs font-medium flex items-center gap-1.5 transition disabled:opacity-50"
            >
              <Minimize2 className="w-3.5 h-3.5 text-emerald-400" /> Özetle
            </button>
            <button
              type="button"
              onClick={() => handleRewrite('TRANSLATE_EN')}
              disabled={isLoading || !currentText.trim()}
              className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-indigo-600 text-slate-200 hover:text-white text-xs font-medium flex items-center gap-1.5 transition disabled:opacity-50"
            >
              <Languages className="w-3.5 h-3.5 text-purple-400" /> English
            </button>
          </div>

          {/* Original Text */}
          <div>
            <label className="text-[11px] text-slate-400 block mb-1">Orijinal Metin:</label>
            <textarea
              rows={3}
              value={currentText}
              onChange={(e) => setCurrentText(e.target.value)}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-white text-xs focus:outline-none focus:border-indigo-500"
            />
          </div>

          {/* AI Result */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] text-indigo-300 font-semibold flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-indigo-400" /> AI Önerisi:
              </label>
              {improvedText && (
                <button
                  type="button"
                  onClick={handleCopy}
                  className="text-[10px] text-slate-400 hover:text-white flex items-center gap-1"
                >
                  <Copy className="w-3 h-3" /> {copied ? 'Kopyalandı' : 'Kopyala'}
                </button>
              )}
            </div>

            {isLoading ? (
              <div className="p-6 rounded-lg bg-slate-950 border border-slate-800 flex items-center justify-center gap-2 text-xs text-indigo-300">
                <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
                Metin optimize ediliyor...
              </div>
            ) : (
              <textarea
                rows={4}
                value={improvedText}
                onChange={(e) => setImprovedText(e.target.value)}
                placeholder="Yukarıdaki butonlardan birine tıklayarak AI ile yeniden yazın..."
                className="w-full px-3 py-2 bg-slate-950 border border-indigo-500/50 rounded-lg text-indigo-100 text-xs focus:outline-none focus:border-indigo-400 placeholder-slate-600"
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t border-slate-800 bg-slate-950 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-slate-400 hover:text-white transition"
          >
            Vazgeç
          </button>
          <button
            type="button"
            onClick={() => {
              if (improvedText) onApply(improvedText);
              onClose();
            }}
            disabled={!improvedText}
            className="px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-50"
          >
            <Check className="w-3.5 h-3.5" />
            Metni Uygula
          </button>
        </div>
      </div>
    </div>
  );
};

export default AICopywriterModal;
